import * as React from "react"

import { cn } from "@/lib/utils"
import { INPUT_LIMITS, safeTruncate } from "@/lib/input-limits"

/**
 * Shared Textarea component.
 *
 * P0-3 hardening: applies a default `maxLength` of 5000 chars (the
 * description / notes limit) and a defensive truncation in onChange.
 * Override via explicit `maxLength={NUMBER}`.
 */
const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, maxLength, onChange, ...props }, ref) => {
  const effectiveMax =
    typeof maxLength === "number" && maxLength > 0
      ? maxLength
      : INPUT_LIMITS.description;

  const guardedOnChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      try {
        const v = e.target.value;
        if (typeof v === "string" && v.length > effectiveMax) {
          const truncated = safeTruncate(v, effectiveMax);
          try { (e.target as HTMLTextAreaElement).value = truncated; } catch {}
        }
        onChange?.(e);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[Textarea] onChange guard caught error", err);
      }
    },
    [onChange, effectiveMax],
  );

  return (
    <textarea
      maxLength={effectiveMax}
      onChange={guardedOnChange}
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Textarea.displayName = "Textarea"

export { Textarea }
